import { broadcastToSenders } from './CastReceiverManager'
import { MessageType } from '../../constants/namespaces'
import { ReceiverState } from '../state/ReceiverStore'

/**
 * Broadcasts a state update to all connected senders
 * @param state The current receiver state
 */
export function broadcastStateUpdate(state: ReceiverState): void {
  const { recipe, currentStepIndex, showingIngredients, timerActive, timerRemaining, servings } = state

  if (!recipe) {
    return
  }

  const currentStepId = recipe.steps && recipe.steps[currentStepIndex] 
    ? recipe.steps[currentStepIndex].id 
    : null

  broadcastToSenders({
    type: MessageType.RECEIVER_STATE_UPDATE,
    currentStepId,
    currentStepIndex,
    showingIngredients,
    timerActive,
    timerRemaining,
    servings,
    timestamp: Date.now()
  })
}

/**
 * Notifies senders about a step change
 * @param stepIndex The new step index
 */
export function notifyStepChange(stepIndex: number): void {
  broadcastToSenders({
    type: MessageType.RECEIVER_STATE_UPDATE,
    currentStepIndex: stepIndex,
    timestamp: Date.now()
  })
}

/**
 * Notifies senders about ingredients view change
 * @param visible Whether ingredients are visible
 */
export function notifyIngredientsToggle(visible: boolean): void {
  broadcastToSenders({
    type: MessageType.RECEIVER_STATE_UPDATE,
    showingIngredients: visible,
    timestamp: Date.now()
  })
}

/**
 * Sends a device capabilities update to all senders
 * @param hasTouchScreen Whether the device has a touch screen
 * @param displayType The type of display (TV, monitor, etc.)
 * @param width The width of the display
 * @param height The height of the display
 */
export function broadcastDeviceCapabilities(
  hasTouchScreen: boolean,
  displayType: 'TV' | 'MONITOR' | 'TABLET' | 'PHONE' | 'UNKNOWN',
  width: number,
  height: number
): void {
  broadcastToSenders({
    type: MessageType.RECEIVER_CAPABILITIES,
    hasTouchScreen,
    displayType,
    width,
    height,
    timestamp: Date.now()
  })
}

/**
 * Sends a content loaded acknowledgment to all senders
 * @param contentId The ID of the loaded content
 * @param additionalData Any additional data to include
 */
export function broadcastContentLoaded(contentId: string, additionalData?: Record<string, any>): void {
  broadcastToSenders({
    type: MessageType.CONTENT_LOADED,
    contentId,
    data: additionalData,
    timestamp: Date.now()
  })
}

/**
 * Sends an error message to all senders
 * @param code The error code
 * @param message The error message
 * @param details Any additional error details
 */
export function broadcastError(code: string, message: string, details?: any): void {
  broadcastToSenders({
    type: MessageType.ERROR,
    data: {
      code,
      message,
      details
    },
    timestamp: Date.now()
  })
}
